import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { CommonStoreStatus } from './CommonStoreStatus'
import { Identity, AccessLevel } from '../model/Identity'
import { FileUploadRequest } from '../requests/FileUploadRequest'
import { ExternalAccessProvisioningRequest } from '../requests/ExternalAccessProvisioningRequest'

import IdentityEndpoints from '../endpoints/IdentityEndpoints'
import AttachmentEndpoints from '../endpoints/AttachmentEndpoints'
import AuthEndpoints from '../endpoints/AuthEndpoints'

interface IdentityState {
  // Core state
  status: CommonStoreStatus
  identity: Identity | null
  
  // Loading management
  waitingFor: number
  
  // Actions
  initialize: (identityId: number) => void
  refresh: () => Promise<Identity>
  updateField: (field: string, value: string) => Promise<Identity>
  updateVaccineField: (field: string, value: string) => Promise<Identity>
  updateEventProvisioning: (
    eventId: number,
    roleId: number,
    secondaryRoleName?: string,
    accreditationLevelId?: number | null
  ) => Promise<Identity>
  deleteEventProvisioning: (eventId: number) => Promise<Identity>
  updateClientProvisioning: (
    clientId: number,
    accessLevel: AccessLevel
  ) => Promise<Identity>
  deleteClientProvisioning: (clientId: number) => Promise<Identity>
  provisionExternalAccess: (
    request: ExternalAccessProvisioningRequest
  ) => Promise<Identity>
  removeExternalAccessProvisioning: (
    provisioningId: number
  ) => Promise<Identity>
  uploadAttachment: (request: FileUploadRequest) => Promise<Identity>
  discardAttachment: (fileId: number) => Promise<Identity>
  resetPassword: () => Promise<void>
  destroy: () => void
  
  // Loading helpers
  startLoading: () => void
  doneLoading: () => void
  
  // Computed getters
  isLoading: () => boolean
  fullName: () => string
}

// Factory function to create identity store instances
export const createIdentityStore = (identityId: number) => {
  return create<IdentityState>()(
    devtools(
      (set, get) => ({
        // Initial state
        status: CommonStoreStatus.LOADING,
        identity: null,
        waitingFor: 0,
        
        // Actions
        initialize: (id: number) => {
          // Store the identity ID for use in actions
          ;(get() as any)._identityId = id
        },
        
        refresh: async () => {
          const { startLoading, doneLoading } = get()
          const identityId = (get() as any)._identityId
          
          startLoading()
          try {
            const identity = await IdentityEndpoints.getIdentity(identityId)
            set({ 
              identity, 
              status: CommonStoreStatus.READY 
            })
            doneLoading()
            return identity
          } catch (error) {
            console.error('Error in IdentityStore', error)
            set({ status: CommonStoreStatus.ERROR })
            doneLoading()
            throw error
          }
        },
        
        updateField: async (field: string, value: string) => {
          const identityId = (get() as any)._identityId
          
          try {
            const identity = await IdentityEndpoints.editIdentity(identityId, {
              field,
              value: typeof value === 'string' ? value.trim() : value
            })
            set({ identity })
            return identity
          } catch (error) {
            console.error('Error in IdentityStore', error)
            throw error
          }
        },
        
        updateVaccineField: async (field: string, value: string) => {
          const identityId = (get() as any)._identityId
          
          try {
            const identity = await IdentityEndpoints.editIdentityVaccineField(identityId, {
              field,
              value
            })
            set({ identity })
            return identity
          } catch (error) {
            console.error('Error in IdentityStore', error)
            throw error
          }
        },
        
        updateEventProvisioning: async (
          eventId: number,
          roleId: number,
          secondaryRoleName: string = '',
          accreditationLevelId: number | null = null
        ) => {
          const identityId = (get() as any)._identityId
          
          try {
            const identity = await IdentityEndpoints.provisionForEvent(identityId, eventId, {
              roleId,
              secondaryRoleName,
              accreditationLevelId
            })
            set({ identity })
            return identity
          } catch (error) {
            console.error('Error in IdentityStore', error)
            throw error
          }
        },
        
        deleteEventProvisioning: async (eventId: number) => {
          const identityId = (get() as any)._identityId
          
          try {
            const identity = await IdentityEndpoints.deleteEventProvisioning(identityId, eventId)
            set({ identity })
            return identity
          } catch (error) {
            console.error('Error in IdentityStore', error)
            throw error
          }
        },
        
        updateClientProvisioning: async (
          clientId: number,
          accessLevel: AccessLevel
        ) => {
          const identityId = (get() as any)._identityId
          
          try {
            const identity = await IdentityEndpoints.provisionForClient(identityId, clientId, {
              accessLevel
            })
            set({ identity })
            return identity
          } catch (error) {
            console.error('Error in IdentityStore', error)
            throw error
          }
        },
        
        deleteClientProvisioning: async (clientId: number) => {
          const identityId = (get() as any)._identityId
          
          try {
            const identity = await IdentityEndpoints.deleteClientProvisioning(
              identityId,
              clientId
            )
            set({ identity })
            return identity
          } catch (error) {
            console.error('Error in IdentityStore', error)
            throw error
          }
        },
        
        provisionExternalAccess: async (
          request: ExternalAccessProvisioningRequest
        ) => {
          const identityId = (get() as any)._identityId
          
          try {
            const identity = await IdentityEndpoints.provisionExternalAccess(identityId, request)
            set({ identity })
            return identity
          } catch (error) {
            console.error('Error in IdentityStore', error)
            throw error
          }
        },
        
        removeExternalAccessProvisioning: async (provisioningId: number) => {
          try {
            const identity = await IdentityEndpoints.removeExternalAccessProvisioning(provisioningId)
            set({ identity })
            return identity
          } catch (error) {
            console.error('Error in IdentityStore', error)
            throw error
          }
        },
        
        uploadAttachment: async (request: FileUploadRequest) => {
          const { startLoading, doneLoading, identity } = get()
          
          startLoading()
          try {
            const updatedIdentity = await AttachmentEndpoints.uploadFile(
              identity!.attachments.id,
              request
            )
            set({ identity: updatedIdentity })
            doneLoading()
            return updatedIdentity
          } catch (error) {
            console.error('Error in IdentityStore', error)
            doneLoading()
            throw error
          }
        },
        
        discardAttachment: async (fileId: number) => {
          const { startLoading, doneLoading } = get()
          
          startLoading()
          try {
            const identity = await AttachmentEndpoints.discardFile(fileId)
            set({ identity })
            doneLoading()
            return identity
          } catch (error) {
            console.error('Error in IdentityStore', error)
            doneLoading()
            throw error
          }
        },
        
        resetPassword: async () => {
          const { startLoading, doneLoading } = get()
          const identityId = (get() as any)._identityId
          
          startLoading()
          try {
            await AuthEndpoints.resetIdentityPassword(identityId)
            doneLoading()
          } catch (error) {
            console.error('Error in IdentityStore', error)
            doneLoading()
            throw error
          }
        },
        
        destroy: () => {
          // Reset to initial state
          set({
            status: CommonStoreStatus.LOADING,
            identity: null,
            waitingFor: 0
          })
        },
        
        // Loading helpers
        startLoading: () => set((state) => ({ 
          waitingFor: state.waitingFor + 1 
        })),
        
        doneLoading: () => set((state) => ({ 
          waitingFor: Math.max(0, state.waitingFor - 1) 
        })),
        
        // Computed getters
        isLoading: () => get().waitingFor > 0,
        
        fullName: () => {
          const { identity } = get()
          if (!identity) return ''
          return `${identity.firstName} ${identity.lastName}`
        }
      }),
      {
        name: `identity-store-${identityId}`,
      }
    )
  )
}

// Export a function to create identity stores
export const useIdentityStore = (identityId: number) => {
  // Create a new store instance for each identity ID
  const store = createIdentityStore(identityId)
  
  // Initialize the store with the identity ID
  store.getState().initialize(identityId)
  
  return store
}